import React, { useEffect, useState, useCallback, useMemo } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Row, Col, Form, Dropdown, Button, Nav } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { getCurrentSemester } from "../utils/teachingUtils";

const API = process.env.REACT_APP_API;
const DAYS = [0, 1, 2, 3, 4, 5, 6];
const DAY_NAMES = { 0: "อาทิตย์", 1: "จันทร์", 2: "อังคาร", 3: "พุธ", 4: "พฤหัส", 5: "ศุกร์", 6: "เสาร์" };
const THAI_MONTHS = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];

// สถานะการเข้าสอน
const STATUS_COLORS = {
  ontime:     { bg: "#d1e7dd", border: "#198754", text: "#0f5132" }, // เขียว — เช็คชื่อในเวลาคาบ
  late:       { bg: "#fff3cd", border: "#f0ad4e", text: "#8a6d3b" }, // เหลือง — เช็คชื่อนอกเวลาคาบ
  missed:     { bg: "#f8d7da", border: "#dc3545", text: "#842029" }, // แดง — ขาดสอน (เลยเวลาแล้วไม่เช็คชื่อ)
  substitute: { bg: "#cfe2ff", border: "#0d6efd", text: "#084298" }, // ฟ้า — มีการจัดสอนแทน
  pending:    { bg: "#f8f9fa", border: "#dee2e6", text: "#6c757d" }, // เทา — ยังไม่ถึงเวลาสอน
  no_teaching:{ bg: "#e2d9f3", border: "#6f42c1", text: "#432874" }, // ม่วง — งดการเรียนการสอน (ปฏิทินปฏิบัติงาน)
};

const fmtYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fmtThaiShort = (d) => `${d.getDate()} ${THAI_MONTHS[d.getMonth()]}`;

const getWeekStart = (d) => {
  const w = new Date(d);
  w.setHours(0, 0, 0, 0);
  w.setDate(w.getDate() - w.getDay()); // เริ่มสัปดาห์วันอาทิตย์ (ตรงกับ teach_day 0)
  return w;
};

// เกณฑ์เช็คชื่อตรงเวลา: ครูต้องเช็คชื่อภายใน 15 นาทีแรกของคาบ — แท็บ "ยังไม่เช็คชื่อ"
// และ "เช็คชื่อสาย" ใช้เส้นนี้ ต่างจากตารางรายห้องที่ถือทั้งคาบเป็นช่วงตรงเวลา
const GRACE_MIN = 15;

const toMin = (hhmm) => {
  const [h, m] = String(hhmm || "0:0").split(":").map(Number);
  return h * 60 + m;
};

const fmtThaiFull = (ymd) => {
  const [y, m, d] = String(ymd).split("-").map(Number);
  return `${d} ${THAI_MONTHS[m - 1]} ${y + 543}`;
};

const roomLabelOf = (r) => `ม.${r.c_id}/${r.r_no}`;

// เหตุที่ครูไม่อยู่: ลา (leave_type) หรือไปอบรม/ประชุมที่อนุมัติแล้ว (training_type)
const awayLabelOf = (r) =>
  r.leave_type
    ? `ครูลา (${r.leave_type})`
    : r.training_type
      ? (r.training_type === "meeting" ? "ไปประชุม" : r.training_type === "activity" ? "ไปกิจกรรม" : "ไปอบรม")
      : null;

export default function TeachingEntryMonitor() {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (s) => {
    const tables = document.getElementsByTagName("table");
    for (const table of tables) {
      const theads = table.querySelectorAll("thead");
      if (s === "dark") {
        for (const thead of theads) { thead.classList.remove("table-secondary"); thead.classList.add("table-primary"); }
        table.classList.add("table-dark");
      } else {
        table.classList.remove("table-dark");
        for (const thead of theads) { thead.classList.remove("table-primary"); thead.classList.add("table-secondary"); }
      }
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const { setLoading } = useLoading();

  const [yearId, setYearId] = useState(() => localStorage.getItem("yearId") || null);
  const [yearLabel, setYearLabel] = useState("");
  const [yearList, setYearList] = useState([]);
  const [roomList, setRoomList] = useState([]);
  const [roomId, setRoomId] = useState("");
  const [periods, setPeriods] = useState([]);
  const [slots, setSlots] = useState([]);
  const [semester, setSemester] = useState(getCurrentSemester());
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));

  const authHeader = useCallback(() => ({
    headers: { authorization: `Bearer ${getToken()}` },
  }), []);

  // ─── Load year list ───────────────────────────────────────────────────────
  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`, authHeader()).then((res) => {
      const list = res.data;
      setYearList(list || []);
      let id = localStorage.getItem("yearId");
      if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem("yearId", id); }
      setYearId(id);
      const sel = list.find((y) => String(y.year_id) === String(id));
      setYearLabel(sel ? `ปีการศึกษา ${sel.year_year}` : "");
    }).catch(() => {});
  }, []);

  const handleSelectYear = (id) => {
    const sel = yearList.find((y) => y.year_id === id);
    if (!sel) return;
    setYearId(id);
    setYearLabel(`ปีการศึกษา ${sel.year_year}`);
    localStorage.setItem("yearId", id);
  };

  // ─── Load room list ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!yearId) return;
    axiosInstance.get(`${API}/getRoomListByYear/${yearId}`, authHeader())
      .then((res) => {
        const list = res.data || [];
        setRoomList(list);
        setRoomId((prev) =>
          list.some((r) => String(r.r_id) === String(prev)) ? prev : (list[0] ? String(list[0].r_id) : "")
        );
      })
      .catch(() => { setRoomList([]); setRoomId(""); });
  }, [yearId]);

  // ─── Load periods (เฉพาะคาบ 5-10 ช่วงเรียนสามัญ) ────────────────────────
  useEffect(() => {
    if (!yearId) return;
    axiosInstance.get(`${API}/getPeriodList/${yearId}`, authHeader())
      .then((res) => {
        setPeriods(
          res.data
            .filter((p) => p.period_order >= 5 && p.period_order <= 10)
            .map((p) => ({
              ...p,
              period_begin: p.period_begin?.slice(0, 5),
              period_end: p.period_end?.slice(0, 5),
            }))
        );
      }).catch(() => setPeriods([]));
  }, [yearId]);

  // ─── Load entry status for the selected week ─────────────────────────────
  useEffect(() => {
    if (!roomId) { setSlots([]); return; }
    const start = fmtYMD(weekStart);
    const end = fmtYMD(new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 6));
    setLoading(true);
    axiosInstance.get(`${API}/getTeachingEntryStatusByRoom/${roomId}/${start}/${end}`, authHeader())
      .then((res) => setSlots(res.data || []))
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, [roomId, weekStart]);

  // ─── แท็บติดตามการเช็คชื่อรายวัน (ยังไม่เช็คชื่อ / เช็คชื่อสาย) ──────────
  // ทั้งสองแท็บอ่านจาก endpoint เดียวกัน ต่างกันแค่วันที่และวิธีคัดแถว
  const todayStr = fmtYMD(new Date());
  const [tab, setTab] = useState("grid");
  const [timing, setTiming] = useState({ date: null, now: null, rows: [] });
  const [timingDate, setTimingDate] = useState(todayStr);
  const [timingLoading, setTimingLoading] = useState(false);
  const [timingTick, setTimingTick] = useState(0);
  const [timingSearch, setTimingSearch] = useState("");
  const [includePast, setIncludePast] = useState(false);

  // แท็บ "ตอนนี้" ผูกกับวันนี้เสมอ ส่วนแท็บ "เช็คชื่อสาย" ย้อนวันได้
  const timingTarget = tab === "late" ? timingDate : todayStr;

  useEffect(() => {
    if (tab === "grid" || !yearId) return;
    let cancelled = false;
    const load = () => {
      setTimingLoading(true);
      axiosInstance
        .get(`${API}/getTeachingCheckTimingByDate/${timingTarget}/${yearId}/${semester}`, authHeader())
        .then((res) => {
          if (cancelled) return;
          const d = res.data || {};
          setTiming({ date: d.date || timingTarget, now: d.now || null, rows: Array.isArray(d.rows) ? d.rows : [] });
        })
        .catch(() => { if (!cancelled) setTiming({ date: timingTarget, now: null, rows: [] }); })
        .finally(() => { if (!cancelled) setTimingLoading(false); });
    };
    load();
    // แท็บ "ตอนนี้" ต้องเดินตามเวลาจริง — ไม่ใช้ setLoading ของทั้งหน้า
    // เพราะสปินเนอร์ทับจอทุกนาทีระหว่างที่หัวหน้าวิชาการกำลังอ่านรายชื่ออยู่
    if (tab !== "live") return () => { cancelled = true; };
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [tab, timingTarget, yearId, semester, timingTick]);

  const shiftTimingDate = (n) => {
    const [y, m, d] = timingDate.split("-").map(Number);
    setTimingDate(fmtYMD(new Date(y, m - 1, d + n)));
  };

  // เวลาปัจจุบันมาจาก server — นาฬิกาเครื่องที่ตั้งเพี้ยนจะทำให้ทั้งโรงเรียนดูเป็นครูที่ยังไม่เช็คชื่อ
  const nowMin = timing.now ? toMin(timing.now.slice(11)) : null;

  const timingFilter = useCallback((r) => {
    if (r.no_teaching) return false; // ปฏิทินระบุงดการเรียนการสอน ไม่ใช่การไม่เช็คชื่อ
    const q = timingSearch.trim().toLowerCase();
    if (!q) return true;
    return [r.t_name, r.sub_teacher_name, r.sub_subid, r.sub_subname, roomLabelOf(r), r.p_code]
      .some((v) => String(v || "").toLowerCase().includes(q));
  }, [timingSearch]);

  // ยังไม่เช็คชื่อ — คาบที่เริ่มไปแล้วแต่ยังไม่มีการเช็คชื่อ
  // ended = เลยเวลาเลิกคาบแล้ว (นับเป็นขาดสอน ไม่ใช่แค่ช้า)
  const liveRows = useMemo(() => {
    if (nowMin == null || timing.date !== todayStr) return [];
    return timing.rows
      .filter((r) => !r.check_at && toMin(r.period_begin) <= nowMin)
      .filter(timingFilter)
      .map((r) => ({
        ...r,
        elapsed: nowMin - toMin(r.period_begin),
        ended: nowMin > toMin(r.period_end),
      }))
      .filter((r) => includePast || !r.ended)
      .sort((a, b) => Number(a.ended) - Number(b.ended) || b.elapsed - a.elapsed
        || a.c_id - b.c_id || a.r_no - b.r_no);
  }, [timing, nowMin, todayStr, includePast, timingFilter]);

  const liveCount = useMemo(() => ({
    overdue: liveRows.filter((r) => !r.ended && r.elapsed > GRACE_MIN).length,
    grace: liveRows.filter((r) => !r.ended && r.elapsed <= GRACE_MIN).length,
    ended: liveRows.filter((r) => r.ended).length,
  }), [liveRows]);

  // เช็คชื่อสาย — เช็คชื่อแล้วแต่เลย 15 นาทีแรกของคาบ
  // แถวที่เช็คคนละวันกับวันสอน = ย้อนหลังมาเช็ค (lateMin คำนวณไม่ได้ ให้ขึ้นบนสุด)
  const lateRows = useMemo(() => {
    const checked = timing.rows.filter((r) => r.check_at).filter(timingFilter);
    return checked
      .map((r) => {
        const sameDay = r.check_at.slice(0, 10) === timing.date;
        return { ...r, sameDay, lateMin: sameDay ? toMin(r.check_at.slice(11)) - toMin(r.period_begin) : null };
      })
      .filter((r) => !r.sameDay || r.lateMin > GRACE_MIN)
      .sort((a, b) => Number(a.sameDay) - Number(b.sameDay) || (b.lateMin || 0) - (a.lateMin || 0));
  }, [timing, timingFilter]);

  const lateTotal = useMemo(
    () => timing.rows.filter((r) => r.check_at && !r.no_teaching).length,
    [timing]
  );

  // ─── Derived ──────────────────────────────────────────────────────────────
  const weekDates = useMemo(
    () => DAYS.map((i) => new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + i)),
    [weekStart]
  );

  const grid = useMemo(() => {
    const g = {};
    slots.forEach((row) => {
      if (String(row.sub_semester) !== String(semester)) return;
      if (!g[row.teach_day]) g[row.teach_day] = {};
      g[row.teach_day][row.period_id] = row;
    });
    return g;
  }, [slots, semester]);

  // สถานะของแต่ละช่อง: ontime / late / missed / substitute / pending
  // จับกลุ่มคาบติดกันของวิชาเดียวกัน (teach_id เดียวกัน) เป็นบล็อกเดียว —
  // เช็คชื่อครั้งเดียวภายในช่วงเวลาของบล็อกถือว่าเข้าสอนตรงเวลาทุกคาบในบล็อก
  const statusMap = useMemo(() => {
    const map = {};
    const now = new Date();
    DAYS.forEach((d) => {
      const daySlots = Object.values(grid[d] || {}).sort((a, b) => a.period_order - b.period_order);
      const dateStr = fmtYMD(weekDates[d]);
      let i = 0;
      while (i < daySlots.length) {
        const run = [daySlots[i]];
        while (
          i + run.length < daySlots.length &&
          daySlots[i + run.length].teach_id === run[0].teach_id &&
          daySlots[i + run.length].period_order === run[run.length - 1].period_order + 1
        ) {
          run.push(daySlots[i + run.length]);
        }
        const checks = run.filter((s) => s.check_time);
        let base;
        if (checks.length > 0) {
          const earliest = checks.reduce((a, b) =>
            `${a.check_date} ${a.check_time}` <= `${b.check_date} ${b.check_time}` ? a : b
          );
          const onTime =
            earliest.check_date === dateStr &&
            earliest.check_time >= run[0].period_begin &&
            earliest.check_time <= run[run.length - 1].period_end;
          base = onTime ? "ontime" : "late";
        } else {
          const [eh, em] = (run[run.length - 1].period_end || "23:59").split(":");
          const end = new Date(weekDates[d]);
          end.setHours(Number(eh), Number(em), 0, 0);
          base = now > end ? "missed" : "pending";
        }
        run.forEach((s) => {
          map[`${d}-${s.period_id}`] = {
            // ตัดสินรายคาบ (ไม่ใช่รายบล็อก) — บล็อกอาจคร่อมช่วงงดการเรียนการสอน
            status: s.no_teaching ? "no_teaching" : s.sub_teacher_name ? "substitute" : base,
            checkTime: s.check_time || checks[0]?.check_time || null,
          };
        });
        i += run.length;
      }
    });
    return map;
  }, [grid, weekDates]);

  const roomLabel = useMemo(() => {
    const r = roomList.find((x) => String(x.r_id) === String(roomId));
    return r ? `ม.${r.c_id}/${r.r_no} (${r.p_code})` : "";
  }, [roomList, roomId]);

  const weekLabel = `${fmtThaiShort(weekDates[0])} – ${fmtThaiShort(weekDates[6])} ${weekDates[6].getFullYear() + 543}`;

  const shiftWeek = (n) => {
    setWeekStart((w) => new Date(w.getFullYear(), w.getMonth(), w.getDate() + n * 7));
  };

  // ─── Slot cell ────────────────────────────────────────────────────────────
  const SlotCell = ({ slot, day }) => {
    if (!slot) {
      return <div style={{ minHeight: 62, color: "#ccc", fontSize: "0.7rem", textAlign: "center", paddingTop: 20 }}>—</div>;
    }
    const st = statusMap[`${day}-${slot.period_id}`] || { status: "pending", checkTime: null };
    const colors = STATUS_COLORS[st.status];
    const awayLabel = awayLabelOf(slot);
    const statusLine = {
      ontime: `เช็คชื่อ ${st.checkTime} น.`,
      late: `เช็คชื่อ ${st.checkTime} น. (นอกเวลาคาบ)`,
      missed: awayLabel ? `ขาดสอน — ${awayLabel}` : "ขาดสอน / ไม่เช็คชื่อ",
      substitute: st.checkTime ? `เช็คชื่อ ${st.checkTime} น.` : "ยังไม่เช็คชื่อ",
      pending: awayLabel ? `ยังไม่ถึงเวลาสอน — ${awayLabel}` : "ยังไม่ถึงเวลาสอน",
      no_teaching: `กิจกรรม/หยุดเรียน — ${slot.no_teaching}`,
    }[st.status];
    return (
      <div
        style={{
          minHeight: 62, background: colors.bg, border: `1px solid ${colors.border}`,
          borderRadius: 4, padding: "4px 6px", fontSize: "0.72rem",
        }}
      >
        <div className="fw-semibold" style={{ color: colors.text, lineHeight: 1.2 }}>{slot.sub_subid}</div>
        <div style={{ lineHeight: 1.2, color: "#333" }}>{slot.sub_subname}</div>
        <div style={{ lineHeight: 1.2, color: "#666" }}>
          {slot.t_name || (slot.is_guidance ? "ครูประจำชั้น" : "")}
        </div>
        {st.status === "substitute" && (
          <div className="fw-semibold" style={{ lineHeight: 1.3, color: colors.text }}>
            สอนแทน: {slot.sub_teacher_name}
          </div>
        )}
        <div className="fw-semibold" style={{ lineHeight: 1.3, color: colors.text }}>{statusLine}</div>
      </div>
    );
  };

  const Legend = () => (
    <div className="d-flex align-items-center flex-wrap gap-3" style={{ fontSize: "0.78rem" }}>
      {[
        ["ontime", "เข้าสอน เช็คชื่อในเวลาคาบ"],
        ["late", "เข้าสอน แต่เช็คชื่อนอกเวลาคาบ"],
        ["missed", "ขาดสอน / ไม่เช็คชื่อ (กำกับหากครูลา / ไปอบรม / ไปประชุม)"],
        ["substitute", "จัดสอนแทน"],
        ["pending", "ยังไม่ถึงเวลาสอน"],
        ["no_teaching", "กิจกรรม/หยุดเรียน (ตามปฏิทินปฏิบัติงาน)"],
      ].map(([k, label]) => (
        <span key={k} className="d-inline-flex align-items-center gap-1">
          <span style={{
            display: "inline-block", width: 14, height: 14, borderRadius: 3,
            background: STATUS_COLORS[k].bg, border: `1px solid ${STATUS_COLORS[k].border}`,
          }}></span>
          {label}
        </span>
      ))}
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        {/* Top bar */}
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">หัวหน้าวิชาการ</Link></li>
              <li className="breadcrumb-item active">ติดตามการเข้าสอน</li>
            </ol>
            <h4 className="main-title mb-0">ติดตามการเข้าสอน (คาบ 5–10)</h4>
          </div>
          <Dropdown>
            <Dropdown.Toggle variant="primary" size="sm">
              {yearLabel || "เลือกปีการศึกษา"}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {yearList.map((y) => (
                <Dropdown.Item key={y.year_id} onClick={() => handleSelectYear(y.year_id)}>
                  {`ปีการศึกษา ${y.year_year}`}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <Row className="g-3">
          <Col xs={12}>
            <Card className="card-one">
              <Card.Header className="d-flex flex-column align-items-stretch gap-2">
                <Nav variant="tabs" activeKey={tab} onSelect={(k) => setTab(k || "grid")}>
                  <Nav.Item><Nav.Link eventKey="grid">ตารางรายห้อง</Nav.Link></Nav.Item>
                  <Nav.Item><Nav.Link eventKey="live">ยังไม่เช็คชื่อ (ตอนนี้)</Nav.Link></Nav.Item>
                  <Nav.Item><Nav.Link eventKey="late">เช็คชื่อสาย (เกิน {GRACE_MIN} นาที)</Nav.Link></Nav.Item>
                </Nav>
                {tab === "grid" && (
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Form.Select
                    size="sm"
                    style={{ width: "auto", minWidth: 180 }}
                    value={roomId}
                    onChange={(e) => setRoomId(e.target.value)}
                  >
                    {roomList.length === 0 && <option value="">ไม่พบห้องเรียน</option>}
                    {roomList.map((r) => (
                      <option key={r.r_id} value={r.r_id}>
                        ม.{r.c_id}/{r.r_no} ({r.p_code})
                      </option>
                    ))}
                  </Form.Select>
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${semester === "1" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setSemester("1")}
                    >
                      เทอม 1
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${semester === "2" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setSemester("2")}
                    >
                      เทอม 2
                    </button>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Button variant="outline-secondary" size="sm" onClick={() => shiftWeek(-1)}>
                    <i className="ri-arrow-left-s-line"></i> สัปดาห์ก่อน
                  </Button>
                  <span className="fw-semibold" style={{ fontSize: "0.85rem" }}>{weekLabel}</span>
                  <Button variant="outline-secondary" size="sm" onClick={() => shiftWeek(1)}>
                    สัปดาห์ถัดไป <i className="ri-arrow-right-s-line"></i>
                  </Button>
                  <Button variant="outline-primary" size="sm" onClick={() => setWeekStart(getWeekStart(new Date()))}>
                    สัปดาห์นี้
                  </Button>
                </div>
                </div>
                )}
                {tab !== "grid" && (
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <div className="btn-group" role="group">
                        <button
                          type="button"
                          className={`btn btn-sm ${semester === "1" ? "btn-primary" : "btn-outline-primary"}`}
                          onClick={() => setSemester("1")}
                        >
                          เทอม 1
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${semester === "2" ? "btn-primary" : "btn-outline-primary"}`}
                          onClick={() => setSemester("2")}
                        >
                          เทอม 2
                        </button>
                      </div>
                      <Form.Control
                        size="sm"
                        type="search"
                        placeholder="ค้นหาครู / ห้อง / วิชา"
                        style={{ width: 190 }}
                        value={timingSearch}
                        onChange={(e) => setTimingSearch(e.target.value)}
                      />
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {tab === "late" ? (
                        <React.Fragment>
                          <Button variant="outline-secondary" size="sm" onClick={() => shiftTimingDate(-1)}>
                            <i className="ri-arrow-left-s-line"></i> วันก่อน
                          </Button>
                          <Form.Control
                            size="sm"
                            type="date"
                            style={{ width: 150 }}
                            max={todayStr}
                            value={timingDate}
                            onChange={(e) => setTimingDate(e.target.value || todayStr)}
                          />
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={timingDate >= todayStr}
                            onClick={() => shiftTimingDate(1)}
                          >
                            วันถัดไป <i className="ri-arrow-right-s-line"></i>
                          </Button>
                          <Button variant="outline-primary" size="sm" onClick={() => setTimingDate(todayStr)}>
                            วันนี้
                          </Button>
                        </React.Fragment>
                      ) : (
                        <Form.Check
                          type="switch"
                          id="include-past-periods"
                          label="รวมคาบที่ผ่านไปแล้ววันนี้"
                          checked={includePast}
                          onChange={(e) => setIncludePast(e.target.checked)}
                        />
                      )}
                      <span className="text-muted" style={{ fontSize: "0.78rem" }}>
                        {timingLoading ? "กำลังโหลด…" : timing.now ? `ข้อมูล ณ ${timing.now.slice(11)} น.` : ""}
                      </span>
                      <Button variant="outline-secondary" size="sm" title="โหลดใหม่" onClick={() => setTimingTick((n) => n + 1)}>
                        <i className="ri-refresh-line"></i>
                      </Button>
                    </div>
                  </div>
                )}
              </Card.Header>

              <Card.Body className="p-2">
                {tab === "live" && (
                  <div>
                    <div className="d-flex align-items-center flex-wrap gap-3 px-2 pt-1 pb-2" style={{ fontSize: "0.8rem" }}>
                      <span className="fw-semibold">{fmtThaiFull(todayStr)}</span>
                      <span className="text-danger fw-semibold">เกินกำหนด {liveCount.overdue} คาบ</span>
                      <span style={{ color: "#8a6d3b" }}>อยู่ในช่วงผ่อนผัน {liveCount.grace} คาบ</span>
                      {includePast && <span className="text-muted">จบคาบแล้วยังไม่เช็คชื่อ {liveCount.ended} คาบ</span>}
                      <span className="text-muted">เกณฑ์: ต้องเช็คชื่อภายใน {GRACE_MIN} นาทีแรกของคาบ</span>
                    </div>
                    {liveRows.length === 0 ? (
                      <div className="text-center text-muted py-5">
                        <i className="ri-checkbox-circle-line fs-2 d-block mb-2"></i>
                        {timingLoading ? "กำลังโหลด…" : "ไม่มีคาบที่ครูยังไม่เช็คชื่อในขณะนี้"}
                      </div>
                    ) : (
                      <div style={{ overflowX: "auto" }}>
                        <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: "0.8rem" }}>
                          <thead className="table-secondary">
                            <tr>
                              <th style={{ width: 110 }}>คาบ</th>
                              <th style={{ width: 110 }}>ห้อง</th>
                              <th>วิชา</th>
                              <th style={{ width: 200 }}>ครูผู้สอน</th>
                              <th style={{ width: 200 }}>สถานะ</th>
                              <th style={{ width: 200 }}>หมายเหตุ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {liveRows.map((r) => (
                              <tr key={`${r.teach_id}-${r.period_id}`}>
                                <td>
                                  คาบ {r.period_order}
                                  <div className="text-muted">{r.period_begin}–{r.period_end}</div>
                                </td>
                                <td>
                                  {roomLabelOf(r)}
                                  <div className="text-muted">{r.p_code}</div>
                                </td>
                                <td>
                                  {r.sub_subid}
                                  <div className="text-muted">{r.sub_subname}</div>
                                </td>
                                <td>{r.sub_teacher_name || r.t_name || (r.is_guidance ? "ครูประจำชั้น" : "—")}</td>
                                <td>
                                  {r.ended ? (
                                    <span className="badge bg-secondary">จบคาบแล้ว — ยังไม่เช็คชื่อ</span>
                                  ) : r.elapsed > GRACE_MIN ? (
                                    <span className="badge bg-danger">เกินกำหนด {r.elapsed - GRACE_MIN} นาที</span>
                                  ) : (
                                    <span className="badge bg-warning text-dark">ผ่อนผัน เหลือ {GRACE_MIN - r.elapsed} นาที</span>
                                  )}
                                  <div className="text-muted">เริ่มคาบมาแล้ว {r.elapsed} นาที</div>
                                </td>
                                <td className="text-muted">
                                  {r.sub_teacher_name
                                    ? `สอนแทน${r.t_name ? ` (แทน ${r.t_name})` : ""}`
                                    : awayLabelOf(r) || ""}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {tab === "late" && (
                  <div>
                    <div className="d-flex align-items-center flex-wrap gap-3 px-2 pt-1 pb-2" style={{ fontSize: "0.8rem" }}>
                      <span className="fw-semibold">{fmtThaiFull(timing.date || timingDate)}</span>
                      <span className="text-danger fw-semibold">เช็คชื่อสาย {lateRows.length} คาบ</span>
                      <span className="text-muted">
                        จากคาบที่เช็คชื่อแล้ว {lateTotal} คาบ
                        {lateTotal > 0 ? ` (${Math.round((lateRows.length / lateTotal) * 100)}%)` : ""}
                      </span>
                      <span className="text-muted">เกณฑ์: เช็คชื่อหลังเริ่มคาบเกิน {GRACE_MIN} นาที</span>
                    </div>
                    {lateRows.length === 0 ? (
                      <div className="text-center text-muted py-5">
                        <i className="ri-time-line fs-2 d-block mb-2"></i>
                        {timingLoading ? "กำลังโหลด…" : "ไม่มีครูเช็คชื่อสายในวันที่เลือก"}
                      </div>
                    ) : (
                      <div style={{ overflowX: "auto" }}>
                        <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: "0.8rem" }}>
                          <thead className="table-secondary">
                            <tr>
                              <th style={{ width: 110 }}>คาบ</th>
                              <th style={{ width: 110 }}>ห้อง</th>
                              <th>วิชา</th>
                              <th style={{ width: 200 }}>ครูผู้สอน</th>
                              <th style={{ width: 120 }}>เช็คชื่อเมื่อ</th>
                              <th style={{ width: 160 }}>ช้ากว่ากำหนด</th>
                              <th style={{ width: 180 }}>หมายเหตุ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lateRows.map((r) => (
                              <tr key={`${r.teach_id}-${r.period_id}`}>
                                <td>
                                  คาบ {r.period_order}
                                  <div className="text-muted">{r.period_begin}–{r.period_end}</div>
                                </td>
                                <td>
                                  {roomLabelOf(r)}
                                  <div className="text-muted">{r.p_code}</div>
                                </td>
                                <td>
                                  {r.sub_subid}
                                  <div className="text-muted">{r.sub_subname}</div>
                                </td>
                                <td>{r.sub_teacher_name || r.t_name || (r.is_guidance ? "ครูประจำชั้น" : "—")}</td>
                                <td>{r.sameDay ? `${r.check_at.slice(11)} น.` : r.check_at}</td>
                                <td>
                                  {r.sameDay ? (
                                    <React.Fragment>
                                      <span className={`badge ${r.lateMin > 30 ? "bg-danger" : "bg-warning text-dark"}`}>
                                        เกินกำหนด {r.lateMin - GRACE_MIN} นาที
                                      </span>
                                      <div className="text-muted">หลังเริ่มคาบ {r.lateMin} นาที</div>
                                    </React.Fragment>
                                  ) : (
                                    <span className="badge bg-dark">เช็คย้อนหลังคนละวัน</span>
                                  )}
                                </td>
                                <td className="text-muted">
                                  {r.sub_teacher_name
                                    ? `สอนแทน${r.t_name ? ` (แทน ${r.t_name})` : ""}`
                                    : awayLabelOf(r) || ""}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {tab === "grid" && (<React.Fragment>
                <div className="px-2 pt-1 pb-2">
                  <Legend />
                </div>
                {periods.length === 0 ? (
                  <div className="text-center text-muted py-5">ยังไม่ได้กำหนดคาบเรียนสำหรับปีนี้</div>
                ) : !roomId ? (
                  <div className="text-center text-muted py-5">
                    <i className="ri-calendar-close-line fs-2 d-block mb-2"></i>
                    ไม่พบห้องเรียนในปีการศึกษานี้
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table
                      className="table table-bordered mb-0"
                      style={{ tableLayout: "fixed", minWidth: periods.length * 120 + 90 }}
                    >
                      <thead className="table-secondary">
                        <tr>
                          <th style={{ width: 90, fontSize: "0.78rem" }}>วัน</th>
                          {periods.map((p) => (
                            <th key={p.period_id} className="text-center" style={{ fontSize: "0.73rem" }}>
                              <div className="fw-semibold">คาบ {p.period_order}</div>
                              <div className="text-muted fw-normal">{p.period_begin}–{p.period_end}</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {DAYS.map((d) => (
                          <tr key={d}>
                            <td className="text-center align-middle fw-semibold" style={{ fontSize: "0.78rem" }}>
                              <div>{DAY_NAMES[d]}</div>
                              <div className="text-muted fw-normal" style={{ fontSize: "0.7rem" }}>
                                {fmtThaiShort(weekDates[d])}
                              </div>
                            </td>
                            {(() => {
                              // รวมช่องคาบติดกันของวิชาเดียวกันเป็นช่องเดียว (colspan)
                              const cells = [];
                              let idx = 0;
                              while (idx < periods.length) {
                                const p = periods[idx];
                                const slot = grid[d]?.[p.period_id];
                                let span = 1;
                                if (slot) {
                                  while (idx + span < periods.length) {
                                    const nextP = periods[idx + span];
                                    const nextSlot = grid[d]?.[nextP.period_id];
                                    if (
                                      nextSlot?.teach_id === slot.teach_id &&
                                      nextSlot?.period_order === slot.period_order + span &&
                                      nextP.period_order === p.period_order + span
                                    ) span++;
                                    else break;
                                  }
                                }
                                cells.push(
                                  <td key={p.period_id} colSpan={span} style={{ padding: 3 }}>
                                    <SlotCell slot={slot} day={d} />
                                  </td>
                                );
                                idx += span;
                              }
                              return cells;
                            })()}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {roomLabel && (
                  <div className="text-muted px-2 pt-2" style={{ fontSize: "0.75rem" }}>
                    ห้อง {roomLabel} — แสดงเฉพาะคาบ 5–10 (ช่วงเรียนสามัญ)
                  </div>
                )}
                </React.Fragment>)}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}
